import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { GlobalSettingsProvider } from '@/context/GlobalSettingsContext';
import { CompareProvider } from '@/context/CompareContext';
import { AuthProvider } from '@/context/AuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import CompareBar from '@/components/CompareBar';
import axios from 'axios';
import '@/App.css';
import { lazy, Suspense } from 'react';

// Global axios defaults & interceptors.
//
// CRITICAL CORS NOTE (May 2026 production fix):
// The Emergent edge proxy rewrites response CORS headers, setting
//   Access-Control-Allow-Origin: *
//   Access-Control-Allow-Credentials: true
// in the same response. This is invalid per the CORS spec — every modern
// browser MUST reject responses with `*` + credentials together. We can't
// fix it at the platform layer, so we work around it client-side: never
// send credentials with our requests. Auth is carried entirely by the
// Bearer token in localStorage via the request interceptor below.
//
// withCredentials=false means cookies (e.g. session_token http-only cookie)
// are not sent, but that's fine — Bearer is the source of truth and the
// backend already supports it.
axios.defaults.withCredentials = false;

axios.interceptors.request.use((config) => {
  // Force withCredentials off on every request, even if some code sets
  // `{ withCredentials: true }` per-call. This is the workaround for the
  // platform-level CORS bug described above.
  config.withCredentials = false;

  // Attach Bearer session token from localStorage (set by AuthCallback &
  // email login). This is what authenticates the user on every request.
  const token = localStorage.getItem('hustlehub_session');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry on transient failures (network errors and 502/503/504). Max 1 retry, 1s + jitter.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config || config._retry) {
      return Promise.reject(error);
    }
    const isNetworkError = !error.response && error.message?.includes('Network Error');
    const status = error.response?.status;
    const isTransient5xx = status === 502 || status === 503 || status === 504;
    if (!isNetworkError && !isTransient5xx) {
      return Promise.reject(error);
    }
    config._retry = true;
    const jitter = Math.floor(Math.random() * 250);
    await new Promise(r => setTimeout(r, 1000 + jitter));
    return axios(config);
  }
);

// Global safety net for any uncaught promise rejections (axios errors that
// don't bubble up into ErrorBoundary). Logs without crashing the SPA.
if (typeof window !== 'undefined' && !window.__hustlehub_unhandled_attached) {
  window.__hustlehub_unhandled_attached = true;
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || e.reason?.toString?.() || 'unhandled rejection';
    // eslint-disable-next-line no-console
    console.warn('[unhandledrejection]', msg);
  });
}

// Critical path — loaded eagerly (homepage + auth)
import HomePage from '@/pages/HomePage';
import AuthCallback from '@/components/AuthCallback';
import ProtectedRoute from '@/components/ProtectedRoute';
import ScrollToTop from '@/components/ScrollToTop';

// Lazy-loaded pages — code-split for performance
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ProfileBuilderPage = lazy(() => import('@/pages/ProfileBuilderPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const BrowseOpportunitiesPage = lazy(() => import('@/pages/BrowseOpportunitiesPage'));
const OpportunityDetailPage = lazy(() => import('@/pages/OpportunityDetailPage'));
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const SavedOpportunitiesPage = lazy(() => import('@/pages/SavedOpportunitiesPage'));
const HiddenOpportunitiesPage = lazy(() => import('@/pages/HiddenOpportunitiesPage'));
const FeaturedSuccessPage = lazy(() => import('@/pages/FeaturedSuccessPage'));
const SubmitOpportunityPage = lazy(() => import('@/pages/SubmitOpportunityPage'));
const SubmitterDashboardPage = lazy(() => import('@/pages/SubmitterDashboardPage'));
const ComparePage = lazy(() => import('@/pages/ComparePage'));
const AISearchResultsPage = lazy(() => import('@/pages/AISearchResultsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const AssetIntentPage = lazy(() => import('@/pages/AssetIntentPage'));
const GrowthDashboardPage = lazy(() => import('@/pages/GrowthDashboardPage'));
const IntegrityDashboardPage = lazy(() => import('@/pages/IntegrityDashboardPage'));
const QuickMatchPage = lazy(() => import('@/pages/QuickMatchPage'));
const CitySEOPage = lazy(() => import('@/pages/CitySEOPage'));
const AudienceSEOPage = lazy(() => import('@/pages/AudienceSEOPage'));
const RegionLandingPage = lazy(() => import('@/pages/RegionLandingPage'));
const RetireesHubPage = lazy(() => import('@/pages/RetireesHubPage'));
const BuilderHubPage = lazy(() => import('@/pages/BuilderHubPage'));
const SelfEmploymentHubPage = lazy(() => import('@/pages/SelfEmploymentHubPage'));
const StartYourBusinessPage = lazy(() => import('@/pages/StartYourBusinessPage'));
const USSelfEmploymentHubPage = lazy(() => import('@/pages/USSelfEmploymentHubPage'));
const USStartYourBusinessPage = lazy(() => import('@/pages/USStartYourBusinessPage'));
const EUSelfEmploymentHubPage = lazy(() => import('@/pages/EUSelfEmploymentHubPage'));
const EUStartYourBusinessPage = lazy(() => import('@/pages/EUStartYourBusinessPage'));
const AsiaSelfEmploymentHubPage = lazy(() => import('@/pages/AsiaSelfEmploymentHubPage'));
const AsiaStartYourBusinessPage = lazy(() => import('@/pages/AsiaStartYourBusinessPage'));
const AffiliateDashboardPage = lazy(() => import('@/pages/AffiliateDashboardPage'));
const DataQualityDashboardPage = lazy(() => import('@/pages/DataQualityDashboardPage'));
const TrendingPage = lazy(() => import('@/pages/TrendingPage'));
const NewOpportunitiesPage = lazy(() => import('@/pages/NewOpportunitiesPage'));
const TopRatedPage = lazy(() => import('@/pages/TopRatedPage'));
const NHSHealthcareHubPage = lazy(() => import('@/pages/NHSHealthcareHubPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const HowItWorksPage = lazy(() => import('@/pages/HowItWorksPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const AdminAnalyticsPage = lazy(() => import('@/pages/AdminAnalyticsPage'));
const AdminMonetisationPage = lazy(() => import('@/pages/AdminMonetisationPage'));
const AdminUrlProposalsPage = lazy(() => import('@/pages/AdminUrlProposalsPage'));
const AdminUrlHealthPage = lazy(() => import('@/pages/AdminUrlHealthPage'));
const AdminListingQualityPage = lazy(() => import('@/pages/AdminListingQualityPage'));
const AdminListingsDatabasePage = lazy(() => import('@/pages/AdminListingsDatabasePage'));
const AdminPhase7CorrectionsPage = lazy(() => import('@/pages/AdminPhase7CorrectionsPage'));
const AdminAIBudgetPage = lazy(() => import('@/pages/AdminAIBudgetPage'));
const AdminListingProposalsPage = lazy(() => import('@/pages/AdminListingProposalsPage'));
const IntentPage = lazy(() => import('@/pages/IntentPage'));
const LocalizedSEOPage = lazy(() => import('@/pages/LocalizedSEOPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const SponsoredPolicyPage = lazy(() => import('@/pages/SponsoredPolicyPage'));
const PartnerWithUsPage = lazy(() => import('@/pages/PartnerWithUsPage'));
const EditorialStandardsPage = lazy(() => import('@/pages/EditorialStandardsPage'));
const HowWeEvaluatePage = lazy(() => import('@/pages/HowWeEvaluatePage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'));
const AffiliateDisclosurePage = lazy(() => import('@/pages/AffiliateDisclosurePage'));
const EditorialPolicyPage = lazy(() => import('@/pages/EditorialPolicyPage'));
const TrustSafetyPage = lazy(() => import('@/pages/TrustSafetyPage'));
const EarningsDisclaimerPage = lazy(() => import('@/pages/EarningsDisclaimerPage'));
const HowWeRankPage = lazy(() => import('@/pages/HowWeRankPage'));
const TrustScoreExplainedPage = lazy(() => import('@/pages/TrustScoreExplainedPage'));
const BestComparisonPage = lazy(() => import('@/pages/BestComparisonPage'));
const BestHubPage = lazy(() => import('@/pages/BestHubPage'));
const BestRemoteWorldwidePage = lazy(() => import('@/pages/BestRemoteWorldwidePage'));
const BestRemoteCountryPage = lazy(() => import('@/pages/BestRemoteCountryPage'));
const ToolsResourcesPage = lazy(() => import('@/pages/ToolsResourcesPage'));

const HUSTLE_MESSAGES = [
  "Finding those pennies down the side of the sofa...",
  "Calculating your next holiday fund...",
  "Scanning all verified opportunities for you...",
  "Hustlers hustle. Let's find yours...",
  "Saving up for that dream car? We've got ideas...",
  "Who said money doesn't grow on trees?",
  "Side hustle o'clock. Let's get it...",
  "Your next income stream is loading...",
  "Every pound starts somewhere...",
  "Matching skills to opportunities...",
];

/** Redirect legacy /country/:slug to /:slug (301-equivalent client redirect) */
function CountryRedirect() {
  const params = window.location.pathname.split('/');
  const country = params[2] || '';
  return <Navigate to={`/${country}`} replace />;
}

/** Set noindex for utility/private pages that should not be indexed */
const NOINDEX_PATHS = ['/login', '/dashboard', '/settings', '/admin', '/saved', '/hidden', '/my-analytics', '/submit', '/profile-builder', '/data-quality', '/integrity-dashboard', '/submitter-dashboard', '/affiliate-dashboard', '/growth-dashboard'];

/**
 * Path patterns that must always be noindex (June 2026 GSC review):
 * Previously this list included `/compare/<a>-vs-<b>` and `/country/<short>`
 * — both removed because:
 *   - 404 responses already emit noindex via NotFoundPage (defence in depth).
 *   - Sitemaps don't include those legacy patterns (verified).
 *   - 2-letter country shortcuts now 301-redirect to canonical slugs.
 * Keep this list empty unless there's a route that ALWAYS renders a real
 * page (200) but should never be indexed (e.g., a temporary launch page).
 */
const NOINDEX_PATTERNS = [];

function NoIndexHandler() {
  const location = useLocation();
  React.useEffect(() => {
    const path = location.pathname;
    const shouldNoIndex =
      NOINDEX_PATHS.some(p => path === p || path.startsWith(p + '/')) ||
      NOINDEX_PATTERNS.some(re => re.test(path));
    const meta = document.querySelector('meta[name="robots"]');
    if (meta) {
      meta.setAttribute('content', shouldNoIndex ? 'noindex, nofollow' : 'index, follow');
    }
  }, [location.pathname]);
  return null;
}


function PageLoader() {
  const [msgIndex, setMsgIndex] = React.useState(() => Math.floor(Math.random() * HUSTLE_MESSAGES.length));
  React.useEffect(() => {
    const t = setInterval(() => setMsgIndex(i => (i + 1) % HUSTLE_MESSAGES.length), 2800);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070D1B]">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 border-2 border-teal-500/20 rounded-full" />
          <div className="absolute inset-0 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <div className="absolute inset-2 border-2 border-emerald-400 border-b-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
        </div>
        <p className="text-sm text-slate-400 transition-opacity duration-500 text-center max-w-xs" style={{ fontFamily: 'Outfit' }}>
          {HUSTLE_MESSAGES[msgIndex]}
        </p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment (not query params) for session_id - synchronous check during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <NoIndexHandler />
      <Suspense fallback={<PageLoader />}>
      <main id="main-content">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/profile-builder" element={
          <ProtectedRoute>
            <ProfileBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/browse" element={<BrowseOpportunitiesPage />} />
        <Route path="/opportunity/:id" element={<OpportunityDetailPage />} />
        <Route path="/saved" element={
          <ProtectedRoute>
            <SavedOpportunitiesPage />
          </ProtectedRoute>
        } />
        <Route path="/hidden" element={
          <ProtectedRoute>
            <HiddenOpportunitiesPage />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute adminOnly={true}>
            <AdminDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/featured/success" element={
          <ProtectedRoute>
            <FeaturedSuccessPage />
          </ProtectedRoute>
        } />
        <Route path="/submit" element={
          <ProtectedRoute>
            <SubmitOpportunityPage />
          </ProtectedRoute>
        } />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/ai-search" element={<AISearchResultsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/my-analytics" element={
          <ProtectedRoute>
            <SubmitterDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/categories/:slug" element={<CategoryPage />} />
        <Route path="/country/:country" element={<CountryRedirect />} />
        <Route path="/asset/:slug" element={<AssetIntentPage />} />
        <Route path="/growth" element={
          <ProtectedRoute adminOnly={true}>
            <GrowthDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/integrity" element={
          <ProtectedRoute adminOnly={true}>
            <IntegrityDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/match" element={<QuickMatchPage />} />
        <Route path="/city/:city" element={<CitySEOPage />} />
        <Route path="/audience/:audience" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-london" element={<CitySEOPage />} />
        <Route path="/side-hustles-manchester" element={<CitySEOPage />} />
        <Route path="/side-hustles-birmingham" element={<CitySEOPage />} />
        <Route path="/side-hustles-leeds" element={<CitySEOPage />} />
        <Route path="/side-hustles-glasgow" element={<CitySEOPage />} />
        <Route path="/side-hustles-liverpool" element={<CitySEOPage />} />
        <Route path="/side-hustles-bristol" element={<CitySEOPage />} />
        <Route path="/side-hustles-edinburgh" element={<CitySEOPage />} />
        <Route path="/side-hustles-cardiff" element={<CitySEOPage />} />
        <Route path="/side-hustles-belfast" element={<CitySEOPage />} />
        <Route path="/side-hustles-nottingham" element={<CitySEOPage />} />
        <Route path="/side-hustles-sheffield" element={<CitySEOPage />} />
        <Route path="/side-hustles-newcastle" element={<CitySEOPage />} />
        {/* US City Pages */}
        <Route path="/side-hustles-new-york" element={<CitySEOPage />} />
        <Route path="/side-hustles-los-angeles" element={<CitySEOPage />} />
        <Route path="/side-hustles-chicago" element={<CitySEOPage />} />
        <Route path="/side-hustles-houston" element={<CitySEOPage />} />
        <Route path="/side-hustles-miami" element={<CitySEOPage />} />
        <Route path="/side-hustles-san-francisco" element={<CitySEOPage />} />
        <Route path="/side-hustles-seattle" element={<CitySEOPage />} />
        <Route path="/side-hustles-austin" element={<CitySEOPage />} />
        {/* Canadian City Pages */}
        <Route path="/side-hustles-toronto" element={<CitySEOPage />} />
        <Route path="/side-hustles-vancouver" element={<CitySEOPage />} />
        <Route path="/side-hustles-calgary" element={<CitySEOPage />} />
        <Route path="/side-hustles-montreal" element={<CitySEOPage />} />
        {/* Australian City Pages */}
        <Route path="/side-hustles-sydney" element={<CitySEOPage />} />
        <Route path="/side-hustles-melbourne" element={<CitySEOPage />} />
        <Route path="/side-hustles-brisbane" element={<CitySEOPage />} />
        <Route path="/side-hustles-perth" element={<CitySEOPage />} />
        {/* New Zealand City Pages */}
        <Route path="/side-hustles-auckland" element={<CitySEOPage />} />
        <Route path="/side-hustles-wellington" element={<CitySEOPage />} />
        <Route path="/side-hustles-christchurch" element={<CitySEOPage />} />
        {/* Ireland City Pages */}
        <Route path="/side-hustles-dublin" element={<CitySEOPage />} />
        <Route path="/side-hustles-cork" element={<CitySEOPage />} />
        <Route path="/side-hustles-galway" element={<CitySEOPage />} />
        {/* European City Pages */}
        <Route path="/side-hustles-berlin" element={<CitySEOPage />} />
        <Route path="/side-hustles-munich" element={<CitySEOPage />} />
        <Route path="/side-hustles-hamburg" element={<CitySEOPage />} />
        <Route path="/side-hustles-frankfurt" element={<CitySEOPage />} />
        <Route path="/side-hustles-cologne" element={<CitySEOPage />} />
        <Route path="/side-hustles-paris" element={<CitySEOPage />} />
        <Route path="/side-hustles-lyon" element={<CitySEOPage />} />
        <Route path="/side-hustles-marseille" element={<CitySEOPage />} />
        <Route path="/side-hustles-toulouse" element={<CitySEOPage />} />
        <Route path="/side-hustles-barcelona" element={<CitySEOPage />} />
        <Route path="/side-hustles-madrid" element={<CitySEOPage />} />
        <Route path="/side-hustles-valencia" element={<CitySEOPage />} />
        <Route path="/side-hustles-seville" element={<CitySEOPage />} />
        <Route path="/side-hustles-amsterdam" element={<CitySEOPage />} />
        <Route path="/side-hustles-rotterdam" element={<CitySEOPage />} />
        <Route path="/side-hustles-utrecht" element={<CitySEOPage />} />
        <Route path="/side-hustles-lisbon" element={<CitySEOPage />} />
        <Route path="/side-hustles-porto" element={<CitySEOPage />} />
        <Route path="/side-hustles-rome" element={<CitySEOPage />} />
        <Route path="/side-hustles-milan" element={<CitySEOPage />} />
        <Route path="/side-hustles-florence" element={<CitySEOPage />} />
        <Route path="/side-hustles-brussels" element={<CitySEOPage />} />
        <Route path="/side-hustles-antwerp" element={<CitySEOPage />} />
        <Route path="/side-hustles-stockholm" element={<CitySEOPage />} />
        <Route path="/side-hustles-gothenburg" element={<CitySEOPage />} />
        <Route path="/side-hustles-copenhagen" element={<CitySEOPage />} />
        <Route path="/side-hustles-oslo" element={<CitySEOPage />} />
        <Route path="/side-hustles-helsinki" element={<CitySEOPage />} />
        <Route path="/side-hustles-zurich" element={<CitySEOPage />} />
        <Route path="/side-hustles-geneva" element={<CitySEOPage />} />
        <Route path="/side-hustles-vienna" element={<CitySEOPage />} />
        <Route path="/side-hustles-salzburg" element={<CitySEOPage />} />
        <Route path="/side-hustles-warsaw" element={<CitySEOPage />} />
        <Route path="/side-hustles-krakow" element={<CitySEOPage />} />
        <Route path="/side-hustles-prague" element={<CitySEOPage />} />
        <Route path="/side-hustles-athens" element={<CitySEOPage />} />
        <Route path="/side-hustles-thessaloniki" element={<CitySEOPage />} />
        {/* Asian city SEO pages */}
        <Route path="/side-hustles-tokyo" element={<CitySEOPage />} />
        <Route path="/side-hustles-osaka" element={<CitySEOPage />} />
        <Route path="/side-hustles-hong-kong-city" element={<CitySEOPage />} />
        <Route path="/side-hustles-singapore-city" element={<CitySEOPage />} />
        <Route path="/side-hustles-for-nurses" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-teachers" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-students" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-parents" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-drivers" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-healthcare-workers" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-over-50s" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-shift-workers" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-gap-year-travellers" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-caregivers" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-full-time-workers" element={<AudienceSEOPage />} />
        <Route path="/side-hustles-for-retirees" element={<RetireesHubPage />} />
        <Route path="/launch-your-hustle" element={<BuilderHubPage />} />
        <Route path="/uk/self-employment" element={<SelfEmploymentHubPage />} />
        <Route path="/uk/start-your-business" element={<StartYourBusinessPage />} />
        <Route path="/us/self-employment" element={<USSelfEmploymentHubPage />} />
        <Route path="/us/start-your-business" element={<USStartYourBusinessPage />} />
        {/* EU Self-Employment Hub Pages (Phase 1: IE, DE, FR, NL, ES) */}
        <Route path="/ireland/self-employment" element={<EUSelfEmploymentHubPage countrySlug="ireland" />} />
        <Route path="/ireland/start-your-business" element={<EUStartYourBusinessPage countrySlug="ireland" />} />
        <Route path="/germany/self-employment" element={<EUSelfEmploymentHubPage countrySlug="germany" />} />
        <Route path="/germany/start-your-business" element={<EUStartYourBusinessPage countrySlug="germany" />} />
        <Route path="/france/self-employment" element={<EUSelfEmploymentHubPage countrySlug="france" />} />
        <Route path="/france/start-your-business" element={<EUStartYourBusinessPage countrySlug="france" />} />
        <Route path="/netherlands/self-employment" element={<EUSelfEmploymentHubPage countrySlug="netherlands" />} />
        <Route path="/netherlands/start-your-business" element={<EUStartYourBusinessPage countrySlug="netherlands" />} />
        <Route path="/spain/self-employment" element={<EUSelfEmploymentHubPage countrySlug="spain" />} />
        <Route path="/spain/start-your-business" element={<EUStartYourBusinessPage countrySlug="spain" />} />
        {/* EU Self-Employment Hub Pages (Phase 2: IT, PT, BE, AT, PL, SE) */}
        <Route path="/italy/self-employment" element={<EUSelfEmploymentHubPage countrySlug="italy" />} />
        <Route path="/italy/start-your-business" element={<EUStartYourBusinessPage countrySlug="italy" />} />
        <Route path="/portugal/self-employment" element={<EUSelfEmploymentHubPage countrySlug="portugal" />} />
        <Route path="/portugal/start-your-business" element={<EUStartYourBusinessPage countrySlug="portugal" />} />
        <Route path="/belgium/self-employment" element={<EUSelfEmploymentHubPage countrySlug="belgium" />} />
        <Route path="/belgium/start-your-business" element={<EUStartYourBusinessPage countrySlug="belgium" />} />
        <Route path="/austria/self-employment" element={<EUSelfEmploymentHubPage countrySlug="austria" />} />
        <Route path="/austria/start-your-business" element={<EUStartYourBusinessPage countrySlug="austria" />} />
        <Route path="/poland/self-employment" element={<EUSelfEmploymentHubPage countrySlug="poland" />} />
        <Route path="/poland/start-your-business" element={<EUStartYourBusinessPage countrySlug="poland" />} />
        <Route path="/sweden/self-employment" element={<EUSelfEmploymentHubPage countrySlug="sweden" />} />
        <Route path="/sweden/start-your-business" element={<EUStartYourBusinessPage countrySlug="sweden" />} />
        {/* EU Self-Employment Hub Pages (Phase 3: DK, NO, FI) */}
        <Route path="/denmark/self-employment" element={<EUSelfEmploymentHubPage countrySlug="denmark" />} />
        <Route path="/denmark/start-your-business" element={<EUStartYourBusinessPage countrySlug="denmark" />} />
        <Route path="/norway/self-employment" element={<EUSelfEmploymentHubPage countrySlug="norway" />} />
        <Route path="/norway/start-your-business" element={<EUStartYourBusinessPage countrySlug="norway" />} />
        <Route path="/finland/self-employment" element={<EUSelfEmploymentHubPage countrySlug="finland" />} />
        <Route path="/finland/start-your-business" element={<EUStartYourBusinessPage countrySlug="finland" />} />
        {/* Asia Self-Employment Hub Pages */}
        <Route path="/japan/self-employment" element={<AsiaSelfEmploymentHubPage countrySlug="japan" />} />
        <Route path="/japan/start-your-business" element={<AsiaStartYourBusinessPage countrySlug="japan" />} />
        <Route path="/singapore/self-employment" element={<AsiaSelfEmploymentHubPage countrySlug="singapore" />} />
        <Route path="/singapore/start-your-business" element={<AsiaStartYourBusinessPage countrySlug="singapore" />} />
        <Route path="/hong-kong/self-employment" element={<AsiaSelfEmploymentHubPage countrySlug="hong-kong" />} />
        <Route path="/hong-kong/start-your-business" element={<AsiaStartYourBusinessPage countrySlug="hong-kong" />} />
        <Route path="/affiliate" element={
          <ProtectedRoute adminOnly={true}>
            <AffiliateDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/data-quality" element={
          <ProtectedRoute adminOnly={true}>
            <DataQualityDashboardPage />
          </ProtectedRoute>
        } />
        {/* Legacy 2-letter / common-shortcut country codes → 301 to canonical slugs.
            Google had discovered these via old internal links / external citations. */}
        <Route path="/nz" element={<Navigate to="/new-zealand" replace />} />
        <Route path="/uk-en" element={<Navigate to="/uk" replace />} />
        <Route path="/usa" element={<Navigate to="/us" replace />} />
        <Route path="/aus" element={<Navigate to="/australia" replace />} />
        <Route path="/ie" element={<Navigate to="/ireland" replace />} />
        <Route path="/nl" element={<Navigate to="/netherlands" replace />} />
        <Route path="/de" element={<Navigate to="/germany" replace />} />
        <Route path="/fr" element={<Navigate to="/france" replace />} />
        <Route path="/es" element={<Navigate to="/spain" replace />} />
        <Route path="/it" element={<Navigate to="/italy" replace />} />
        <Route path="/pt" element={<Navigate to="/portugal" replace />} />
        <Route path="/be" element={<Navigate to="/belgium" replace />} />
        <Route path="/se" element={<Navigate to="/sweden" replace />} />
        <Route path="/pl" element={<Navigate to="/poland" replace />} />
        <Route path="/ch" element={<Navigate to="/switzerland" replace />} />
        <Route path="/at" element={<Navigate to="/austria" replace />} />
        <Route path="/jp" element={<Navigate to="/japan" replace />} />
        <Route path="/sg" element={<Navigate to="/singapore" replace />} />
        {/* End legacy redirects */}

        <Route path="/trending" element={<TrendingPage />} />
        <Route path="/new" element={<NewOpportunitiesPage />} />
        <Route path="/top-rated" element={<TopRatedPage />} />
        <Route path="/nhs-opportunities" element={<NHSHealthcareHubPage />} />
        {/* Region Landing Pages (Phase 1: curated SEO pages) */}
        <Route path="/uk" element={<RegionLandingPage regionSlug="uk" />} />
        <Route path="/us" element={<RegionLandingPage regionSlug="us" />} />
        <Route path="/australia" element={<RegionLandingPage regionSlug="australia" />} />
        <Route path="/canada" element={<RegionLandingPage regionSlug="canada" />} />
        <Route path="/remote" element={<RegionLandingPage regionSlug="remote" />} />
        <Route path="/new-zealand" element={<RegionLandingPage regionSlug="new-zealand" />} />
        <Route path="/ireland" element={<RegionLandingPage regionSlug="ireland" />} />
        {/* European country landing pages */}
        <Route path="/germany" element={<RegionLandingPage regionSlug="germany" />} />
        <Route path="/france" element={<RegionLandingPage regionSlug="france" />} />
        <Route path="/spain" element={<RegionLandingPage regionSlug="spain" />} />
        <Route path="/netherlands" element={<RegionLandingPage regionSlug="netherlands" />} />
        <Route path="/portugal" element={<RegionLandingPage regionSlug="portugal" />} />
        <Route path="/italy" element={<RegionLandingPage regionSlug="italy" />} />
        <Route path="/belgium" element={<RegionLandingPage regionSlug="belgium" />} />
        <Route path="/sweden" element={<RegionLandingPage regionSlug="sweden" />} />
        <Route path="/denmark" element={<RegionLandingPage regionSlug="denmark" />} />
        <Route path="/norway" element={<RegionLandingPage regionSlug="norway" />} />
        <Route path="/finland" element={<RegionLandingPage regionSlug="finland" />} />
        <Route path="/switzerland" element={<RegionLandingPage regionSlug="switzerland" />} />
        <Route path="/austria" element={<RegionLandingPage regionSlug="austria" />} />
        <Route path="/poland" element={<RegionLandingPage regionSlug="poland" />} />
        <Route path="/czech-republic" element={<RegionLandingPage regionSlug="czech-republic" />} />
        <Route path="/greece" element={<RegionLandingPage regionSlug="greece" />} />
        {/* New EU country landing pages */}
        <Route path="/bulgaria" element={<RegionLandingPage regionSlug="bulgaria" />} />
        <Route path="/croatia" element={<RegionLandingPage regionSlug="croatia" />} />
        <Route path="/cyprus" element={<RegionLandingPage regionSlug="cyprus" />} />
        <Route path="/estonia" element={<RegionLandingPage regionSlug="estonia" />} />
        <Route path="/hungary" element={<RegionLandingPage regionSlug="hungary" />} />
        <Route path="/iceland" element={<RegionLandingPage regionSlug="iceland" />} />
        <Route path="/latvia" element={<RegionLandingPage regionSlug="latvia" />} />
        <Route path="/lithuania" element={<RegionLandingPage regionSlug="lithuania" />} />
        <Route path="/luxembourg" element={<RegionLandingPage regionSlug="luxembourg" />} />
        <Route path="/malta" element={<RegionLandingPage regionSlug="malta" />} />
        <Route path="/romania" element={<RegionLandingPage regionSlug="romania" />} />
        <Route path="/serbia" element={<RegionLandingPage regionSlug="serbia" />} />
        <Route path="/slovakia" element={<RegionLandingPage regionSlug="slovakia" />} />
        <Route path="/slovenia" element={<RegionLandingPage regionSlug="slovenia" />} />
        {/* Asian landing pages */}
        <Route path="/japan" element={<RegionLandingPage regionSlug="japan" />} />
        <Route path="/singapore" element={<RegionLandingPage regionSlug="singapore" />} />
        <Route path="/hong-kong" element={<RegionLandingPage regionSlug="hong-kong" />} />
        {/* Region-based browse routes (non-landing page regions) */}
        <Route path="/europe" element={<BrowseOpportunitiesPage />} />
        <Route path="/asia" element={<BrowseOpportunitiesPage />} />
        <Route path="/middle-east" element={<BrowseOpportunitiesPage />} />
        <Route path="/africa" element={<BrowseOpportunitiesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/contact" element={<ContactPage />} />
        {/* Intent/Pain-Point Pages */}
        <Route path="/ways-to-earn/:slug" element={<IntentPage />} />
        <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
        <Route path="/admin/monetisation" element={<AdminMonetisationPage />} />
        <Route path="/admin/url-proposals" element={<AdminUrlProposalsPage />} />
        <Route path="/admin/url-health" element={<AdminUrlHealthPage />} />
        <Route path="/admin/listing-quality" element={<AdminListingQualityPage />} />
        <Route path="/admin/listings-database" element={<AdminListingsDatabasePage />} />
        <Route path="/admin/phase7-corrections" element={
          <ProtectedRoute adminOnly={true}>
            <AdminPhase7CorrectionsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/ai-budget" element={
          <ProtectedRoute adminOnly={true}>
            <AdminAIBudgetPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/listing-proposals" element={
          <ProtectedRoute adminOnly={true}>
            <AdminListingProposalsPage />
          </ProtectedRoute>
        } />
        {/* Localized SEO pages (DE/FR/ES/NL/PT/IT/SV/DA/NO/FI/PL/CS/EL) */}
        <Route path="/de/*" element={<LocalizedSEOPage />} />
        <Route path="/fr/*" element={<LocalizedSEOPage />} />
        <Route path="/es/*" element={<LocalizedSEOPage />} />
        <Route path="/nl/*" element={<LocalizedSEOPage />} />
        <Route path="/pt/*" element={<LocalizedSEOPage />} />
        <Route path="/it/*" element={<LocalizedSEOPage />} />
        <Route path="/sv/*" element={<LocalizedSEOPage />} />
        <Route path="/da/*" element={<LocalizedSEOPage />} />
        <Route path="/no/*" element={<LocalizedSEOPage />} />
        <Route path="/fi/*" element={<LocalizedSEOPage />} />
        <Route path="/pl/*" element={<LocalizedSEOPage />} />
        <Route path="/cs/*" element={<LocalizedSEOPage />} />
        <Route path="/el/*" element={<LocalizedSEOPage />} />
        <Route path="/ja/*" element={<LocalizedSEOPage />} />
        <Route path="/zh-hant/*" element={<LocalizedSEOPage />} />
        <Route path="/sponsored-policy" element={<SponsoredPolicyPage />} />
        <Route path="/partner-with-us" element={<PartnerWithUsPage />} />
        <Route path="/partner" element={<Navigate to="/partner-with-us" replace />} />
        <Route path="/partnerships" element={<Navigate to="/partner-with-us" replace />} />
        <Route path="/editorial-standards" element={<EditorialStandardsPage />} />
        <Route path="/standards" element={<Navigate to="/editorial-standards" replace />} />
        <Route path="/how-we-evaluate" element={<HowWeEvaluatePage />} />
        <Route path="/evaluation-methodology" element={<Navigate to="/how-we-evaluate" replace />} />
        {/* ─── Trust / Legal pages ─── */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
        <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
        <Route path="/disclosure" element={<Navigate to="/affiliate-disclosure" replace />} />
        <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
        <Route path="/editorial" element={<Navigate to="/editorial-policy" replace />} />
        <Route path="/trust-safety" element={<TrustSafetyPage />} />
        <Route path="/trust" element={<Navigate to="/trust-safety" replace />} />
        <Route path="/earnings-disclaimer" element={<EarningsDisclaimerPage />} />
        <Route path="/disclaimer" element={<Navigate to="/earnings-disclaimer" replace />} />
        {/* ─── E-E-A-T methodology pages ─── */}
        <Route path="/how-we-rank" element={<HowWeRankPage />} />
        <Route path="/methodology" element={<Navigate to="/how-we-rank" replace />} />
        <Route path="/trust-score-explained" element={<TrustScoreExplainedPage />} />
        <Route path="/trust-score" element={<Navigate to="/trust-score-explained" replace />} />
        {/* ─── Editorial comparison ("Best of") pages ─── */}
        <Route path="/best" element={<BestHubPage />} />
        <Route path="/best/remote-side-hustles-worldwide" element={<BestRemoteWorldwidePage />} />
        <Route path="/best/remote-side-hustles-in/:countrySlug" element={<BestRemoteCountryPage />} />
        <Route path="/best/:slug" element={<BestComparisonPage />} />

        <Route path="/tools-and-resources" element={<ToolsResourcesPage />} />
        <Route path="/tools" element={<Navigate to="/tools-and-resources" replace />} />
        <Route path="/resources" element={<Navigate to="/tools-and-resources" replace />} />
        {/* Catch-all 404 — must be the LAST route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </main>
      </Suspense>
      <CompareBar />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[9999] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-teal-500 focus:text-white focus:rounded-lg focus:text-sm focus:font-semibold">
          Skip to main content
        </a>
        <GlobalSettingsProvider>
          <CompareProvider>
            <BrowserRouter>
              <AuthProvider>
                <AppRouter />
              </AuthProvider>
            </BrowserRouter>
          </CompareProvider>
        </GlobalSettingsProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;
